import { z } from 'zod/v4'

import { defineScreen, type ScreenData, type ScreenQuery } from '../define-screen.js'
import { freshnessSchema } from '../freshness.js'
import { screenSection } from '../section.js'
import { adminPaginationSchema } from './users.screen.js'

/*
 * «Operaciones masivas»: one page of operations, and one operation with a page of its items. The schemas mirror what
 * the services return (`BulkOperationStatusResponse`, `BulkOperationItemsResponse`); the type assertions of
 * `shared-bulk-operations-screen.type-assertions.ts` tie them in both directions. The row of the LIST is what the
 * route sends (`listBulkOperations`): it carries no `succeeded` and no `failed`, which only the status of ONE
 * operation has.
 */

/** The ten values of `BulkOperationType`. */
export const bulkOperationTypeSchema = z.enum([
  'user_invite',
  'user_suspend',
  'user_activate',
  'user_delete',
  'user_role_change',
  'asset_create',
  'asset_update',
  'asset_delete',
  'export_data',
  'import_data',
])

/** The seven values of `BulkOperationStatus`. */
export const bulkOperationStatusValueSchema = z.enum([
  'pending',
  'running',
  'completed',
  'failed',
  'cancelled',
  'undoing',
  'undone',
])

/** One row of the page: the operation and how far it is, nothing the list does not know. */
export const bulkOperationListRowSchema = z.strictObject({
  id: z.string(),
  operationType: bulkOperationTypeSchema,
  status: bulkOperationStatusValueSchema,
  progress: z.strictObject({
    total: z.number(),
    processed: z.number(),
    percentage: z.number(),
  }),
  createdAt: z.string(),
  completedAt: z.string().nullable(),
})
export type BulkOperationListRow = z.infer<typeof bulkOperationListRowSchema>

/** The status of ONE operation: `BulkOperationStatusResponse`. */
export const bulkOperationStatusSchema = z.strictObject({
  id: z.string(),
  operationType: bulkOperationTypeSchema,
  status: bulkOperationStatusValueSchema,
  progress: z.strictObject({
    total: z.number(),
    processed: z.number(),
    succeeded: z.number(),
    failed: z.number(),
    percentage: z.number(),
  }),
  estimatedCompletionAt: z.string().nullable(),
  canUndo: z.boolean(),
  startedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  createdAt: z.string(),
  errorMessage: z.string().nullable().optional(),
})

/** One page of the items of an operation: `BulkOperationItemsResponse`. */
export const bulkOperationItemsSchema = z.strictObject({
  items: z.array(
    z.strictObject({
      id: z.string(),
      entityType: z.string(),
      entityId: z.string(),
      entityName: z.string().nullable(),
      status: z.string(),
      errorMessage: z.string().nullable(),
    }),
  ),
  pagination: z.strictObject({
    total: z.number(),
    page: z.number(),
    limit: z.number(),
  }),
})

/** The same limits as the route this screen replaces (`bulk-operations.validators.ts`); 25 is the page size. */
export const adminBulkOperationsQuerySchema = z.strictObject({
  page: z.coerce.number<number>().int().min(1).default(1),
  limit: z.coerce.number<number>().int().min(1).max(500).default(25),
  status: bulkOperationStatusValueSchema.optional(),
  operationType: bulkOperationTypeSchema.optional(),
})

export const adminBulkOperationsScreen = defineScreen({
  id: 'admin.bulkOperations',
  path: '/api/screens/admin/bulk-operations',
  access: 'super_admin',
  query: adminBulkOperationsQuerySchema,
  response: z.strictObject({
    page: screenSection(
      z.strictObject({
        items: z.array(bulkOperationListRowSchema),
        pagination: adminPaginationSchema,
      }),
    ),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.bulkOperations'],
  budget: { requests: 1, statements: 5, bytes: 20_000, p95Ms: 150 },
  summary: 'Bulk operations: one page of operations, newest first',
})
export type AdminBulkOperationsQuery = ScreenQuery<typeof adminBulkOperationsScreen>
export type AdminBulkOperationsData = ScreenData<typeof adminBulkOperationsScreen>

export const adminBulkOperationDetailScreen = defineScreen({
  id: 'admin.bulkOperations.detail',
  path: '/api/screens/admin/bulk-operations/detail',
  access: 'super_admin',
  query: z.strictObject({
    id: z.guid(),
    itemPage: z.coerce.number<number>().int().min(1).default(1),
    itemLimit: z.coerce.number<number>().int().min(1).max(500).default(50),
  }),
  response: z.strictObject({
    operation: bulkOperationStatusSchema,
    items: screenSection(bulkOperationItemsSchema),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.bulkOperations'],
  budget: { requests: 1, statements: 6, bytes: 40_000, p95Ms: 150 },
  summary: 'Bulk operations › one operation: its status and one page of its items',
})
export type AdminBulkOperationDetailData = ScreenData<typeof adminBulkOperationDetailScreen>
